<script setup lang="ts">
/**
 * Section 47(3) — Notification of Change in the Address Where Registers or Records are Kept. M32017.
 * · records a~k 는 DCR 과 ctx.flag(record_*) 로 동기화.
 * · 두 개의 주소 유형 체크박스(other / to-registered)는 이 문서 로컬 선택(템플릿 [[if]] 아님).
 */
import { ref, inject } from 'vue';
import { CHANGE_BUSINESS_ADDRESS_DOC_CTX } from './changeBusinessAddressContext';
import { RECORD_ITEMS } from './changeBusinessAddressFields';
import ChangeBusinessAddressDocVarField from './ChangeBusinessAddressDocVarField.vue';
import ChangeBusinessAddressSigners from './parts/ChangeBusinessAddressSigners.vue';

const ctx = inject(CHANGE_BUSINESS_ADDRESS_DOC_CTX)!;
const addressType = ref<'' | 'other' | 'toRegistered'>('');
function pick(t: 'other' | 'toRegistered') { addressType.value = addressType.value === t ? '' : t; }
</script>

<template>
  <div class="cba-doc">
    <div class="doc-head">
      <div class="doc-meta">COMPANIES ACT 2016</div>
      <div class="doc-meta">Section 47(3)</div>
      <div class="doc-company notice-title">
        NOTIFICATION OF CHANGE IN THE ADDRESS WHERE REGISTERS OR RECORDS ARE KEPT
      </div>
      <div class="doc-company" style="margin-top:8px"><ChangeBusinessAddressDocVarField field-key="company_name" /></div>
    </div>

    <p class="doc-p">
      <b>New address where registers/records are kept :</b>
      <ChangeBusinessAddressDocVarField field-key="address_line_1" />,
      <ChangeBusinessAddressDocVarField field-key="address_line_2" />,
      <ChangeBusinessAddressDocVarField field-key="address_line_3" />,
      <ChangeBusinessAddressDocVarField field-key="postcode" /> <ChangeBusinessAddressDocVarField field-key="city" />,
      <ChangeBusinessAddressDocVarField field-key="state" />, <ChangeBusinessAddressDocVarField field-key="country" />
    </p>

    <p class="type-col">
      <span class="doc-toggle chk" :class="{ on: addressType === 'other' }" @click="pick('other')">
        {{ addressType === 'other' ? '☑' : '☐' }} Other than registered address*
      </span>
      <span class="doc-toggle chk" :class="{ on: addressType === 'toRegistered' }" @click="pick('toRegistered')">
        {{ addressType === 'toRegistered' ? '☑' : '☐' }} Change from a place other than the registered address to registered address*
      </span>
    </p>

    <p class="doc-lbl-title">Type of registers/records kept at this address :</p>
    <ul class="rec-list">
      <li
        v-for="r in RECORD_ITEMS" :key="r.key"
        class="rec-item doc-toggle" :class="{ off: !ctx.flag(r.key) }"
        :title="ctx.flag(r.key) ? 'included — click to exclude' : 'excluded — click to include'"
        @click="ctx.toggleFlag(r.key)"
      >
        <span class="rec-mark">{{ ctx.flag(r.key) ? '☑' : '☐' }}</span>
        <span class="rec-tx">{{ r.text }} ;</span>
      </li>
    </ul>

    <p class="doc-p">
      <b>Effective date where registers/records are kept at this address :</b>
      <ChangeBusinessAddressDocVarField field-key="effective_date" />
    </p>
    <p class="doc-note">*Please use a different form if the address is different</p>

    <p class="doc-lbl-title">Declaration:</p>
    <p class="doc-p">I confirm that the facts and information stated in this document are true and to the best of my knowledge.</p>

    <p class="doc-p"><b>Signed:</b></p>
    <ChangeBusinessAddressSigners var-key="s47" />
    <p class="doc-p doc-sign-date"><b>Date :</b> <ChangeBusinessAddressDocVarField field-key="declaration_date" /></p>

    <div class="doc-attn">
      <b>Attention:</b>
      It is an offence under section 591 of the Companies Act 2016 to make or authorize the making of a statement
      that a person knows is false or misleading and that person may be liable, upon conviction, to imprisonment
      for a term not exceeding ten years or to a fine not exceeding RM3million or to both.
    </div>
  </div>
</template>

<style src="./changeBusinessAddressDoc.css"></style>
